import { useCallback, useState } from 'react';
import { guideProgress, guideProgressSave, type GuideProgress } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery } from '../../useQuery';
import { useReadOnlyReason } from '../../ui/ReadOnly';

/**
 * The signed-in person's ticks on one guide, from the server (PROPOSED GET/PUT
 * /onboarding/guide). Never kept in the browser: a tick is shown once the server has it.
 * Until the route exists, every step reads not done and a tick says it could not be
 * saved. Read-only roles and View as never write.
 */
export function useGuideProgress(guide: string) {
  const { client } = useSession();
  const readOnly = useReadOnlyReason();
  const q = useQuery<GuideProgress>((signal) => guideProgress(client, { query: { guide }, signal }), [client, guide]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const done: Record<string, string> = q.data?.done ?? {};

  const save = useCallback(
    async (body: { step: string; done: boolean } | { reset: true }) => {
      if (readOnly) return false;
      setSaving(true);
      setSaveError(null);
      try {
        const next = await guideProgressSave(client, { body: { guide, ...body } as never });
        q.setData(() => next);
        return true;
      } catch (e) {
        setSaveError(e instanceof Error && e.message ? e.message : 'Could not save this step.');
        return false;
      } finally {
        setSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, guide, readOnly],
  );

  return {
    done,
    loading: q.loading,
    loadError: q.error,
    saving,
    saveError,
    readOnly,
    setStep: (step: string, isDone: boolean) => save({ step, done: isDone }),
    reset: () => save({ reset: true }),
  };
}
